import { LitState } from 'lit-element-state'
import { handleize, validateEmail } from '../../lib/utils'
import api from '../api'
import { handleRuleResult } from './utility-product-logic'

/**
 * Global state for the quiz app
 * This state will be shared between all components
 *
 */
class QuizState extends LitState {
  /**
   * @returns {Object} state variables
   * @property {string} quizHandle - quiz handle
   * @property {Object} quiz - quiz object (coming from Shopify's metaobjects)
   * @property {boolean} isStatic - quiz is static (no local storage)
   * @property {string} resultsId - quiz results metaobject id
   * @property {string} resultsHandle - quiz results metaobject handle
   * @property {Object[]} responses - quiz responses
   * @property {string} responses[].question - question handle
   * @property {string[]} responses[].selection - selected answers
   * @property {string} responses[].selection[].handle - answer handle
   * @property {string} responses[].selection[].name - answer name
   * @property {Object[]} orderLines - order lines
   * @property {string} orderLines[].id - order line variant id
   * @property {string} orderLines[].variant - order line variant
   * @property {string} orderLines[].quantity - order line quantity
   * @property {boolean} isReady - quiz is ready to be rendered (store has been hydrated)
   * @property {string} currentModal - current modal to be rendered
   * @property {string} currentModalData - current modal data
   * @property {boolean} modalBgVisible - modal background is visible
   * @property {Object} customer - customer data
   * @property {string} customer.name - customer name
   * @property {string} customer.email - customer email
   * @property {string} customer.signupEmail - customer signup email (klaviyo)
   * @property {boolean} cart - cart object
   */
  static get stateVars() {
    return {
      quizHandle: null,
      resultsId: null,
      resultsHandle: null,
      quiz: null,
      isStatic: false,
      isInline: false,
      responses: [],
      orderLines: [],
      isReady: false,
      currentModal: null,
      currentModalData: {},
      modalBgVisible: false,
      customer: {
        id: null,
        name: '',
        email: '',
        signupEmail: '',
        signupOptIn: false
      },
      localStorage: {},
      cart: {
        items: [],
        items_subtotal_price: 0
      }
    }
  }

  get localStorageKey() {
    return `HH_QUIZ_v1_0_${this.quizHandle}` // change version to force local storage reset
  }

  toggleStatic(value) {
    this.isStatic = typeof value !== 'undefined' ? value : !this.isStatic
  }

  set(key, value) {
    if (typeof key === 'object') {
      Object.entries(key).forEach(([k, v]) => this[k] = v)
    } else {
      this[key] = value
    }
  }

  /**
   * Bootstraps the quiz state
   * - Sets the quiz handle
   * - Sets the quiz object based on HH_QUIZ global variable
   * - Sets the responses skeleton
   */
  async bootstrapQuiz(quizHandle, resultsObjectOrHandle) {
    this.quizHandle = quizHandle
    this.quiz = HH_QUIZ[quizHandle]
    let success = false

    if (resultsObjectOrHandle) {
      if (typeof resultsObjectOrHandle === 'object') {
        const obj = resultsObjectOrHandle.json ? {
          ...resultsObjectOrHandle.json,
          customer: resultsObjectOrHandle.customer_json
        } : resultsObjectOrHandle
        try {
          this.loadStorage(obj)
          success = true
        } catch (e) {
        }
      } else {
        success = await this.fetchRemoteResults(resultsObjectOrHandle)
      }
    }
    

    if (!success && !this.isInline && !this.isStatic) {
      this.localStorage = localStorage.getItem(this.localStorageKey) ? JSON.parse(localStorage.getItem(this.localStorageKey)) : null

      if (Date.now() - (this.localStorage?.t ?? 0) > 1000 * 60 * 60) { // 1 hour
        localStorage.removeItem(this.localStorageKey)
        this.localStorage = this
      }

      this.loadStorage(this.localStorage)
      success = true
    } else {
      localStorage.removeItem(this.localStorageKey)
    }

    if (!success) {
      throw new Error('Quiz doesn\'t exist')
    }

    this.isReady = true
  }

  loadStorage({ resultsId, resultsHandle, responses: localResponses, orderLines, customer }) {
    this.resultsId = resultsId
    this.resultsHandle = resultsHandle

    this.customer = {
      id: customer?.id ? customer?.id : HH_QUIZ.customer.id,
      name: customer?.name ? customer?.name : HH_QUIZ.customer.name,
      email: customer?.email ? customer?.email : HH_QUIZ.customer.email,
      signupEmail: customer?.signupEmail ? customer?.signupEmail : HH_QUIZ.customer.signupEmail,
      signupOptIn: customer?.signupOptIn ? customer?.signupOptIn : HH_QUIZ.customer.signupOptIn ?? false
    }

    this.responses = this.orderedQuizQuestions.reduce(
      (responses, question) => [
        ...responses,
        {
          question: question.handle,
          selection: this.getQuestionResponses(question.handle, localResponses),
        },
      ],
      []
    )

    this.orderLines = orderLines.filter((l) =>
      this.selectedProductVariants.map((v) => v.id).includes(l.id)
    )

    this.syncOrderLines()
  }

  async fetchRemoteResults(handle) {
    const response = await api.get({ handle })
    if (response?.status !== 200) return false
    this.loadStorage(response)
    return true
  }

  updateCustomer(data, reactive = true) {
    Object.keys(data).forEach(key => {
      this.customer[key] = data[key]
      if (reactive) this.customer = { ...this.customer }
    })
  }

  /**
   * Gets the current quiz questions organized by a categories tree
   * @returns {Object[]} categories
   * @returns {string} categories[].handle - category handle
   * @returns {string} categories[].name - category name
   * @returns {string} categories[].description - category description
   * @returns {number} categories[].order - category order
   * @returns {number} categories[].progress - category progress
   * @returns {boolean} categories[].isComplete - category is complete
   * @returns {boolean} categories[].isEnabled - category is enabled (an enabled category means all previous categories are complete)
   * @returns {Object[]} categories[].questions - category questions
   * @returns {string} categories[].questions[].handle - question handle
   * @returns {string} categories[].questions[].name - question name
   * @returns {string} categories[].questions[].description - question description
   * @returns {number} categories[].questions[].order - question order
   * @returns {number} categories[].questions[].isComplete - question is completed (has at least one answer selected)
   * @returns {boolean} categories[].questions[].isEnabled - question is enabled (an enabled question means all previous questions are complete)
   * @returns {Object[]} categories[].questions[].responses - question user responses
   * @returns {string} categories[].questions[].responses[] - response handle
   */
  get quizCategories() {
    let order = -1
    // Get categories tree
    const categories = Object.values(
      this.quiz?.questions.reduce((categories, question) => {
        const category = handleize(question.category.handle)
        categories[category] = categories[category] ?? {
          questions: [],
          ...question.category,
          order: ++order,
        }
        categories[category].questions.push({
          ...{},
          ...question,
          responses: this.getQuestionResponses(question.handle),
          isComplete: this.getQuestionResponses(question.handle).length > 0,
          order: categories[category].questions.length + 1,
        })
        const totalQuestions = categories[category].questions.length
        const totalCompleteQuestions = categories[category].questions.filter(
          (q) => q.isComplete
        ).length
        categories[category].progress = totalCompleteQuestions / totalQuestions
        categories[category].isComplete = categories[category].progress === 1
        return categories
      }, {}) ?? {}
    )

    return (
      categories
        // Order categories
        .sort((a, b) => a.order - b.order)
        // Map enabled categories (an enabled category means all previous categories are complete)
        .map((c) => ({
          ...c,
          isEnabled: this.customerIsCompleted && categories
            .filter((cat) => cat.order < c.order)
            .every((cat) => cat.isComplete),
        }))
        // Map enabled questions (an enabled question means all previous questions are complete)
        .map((c) => ({
          ...c,
          questions: c.questions.map((q) => ({
            ...q,
            isEnabled:
              this.customerIsCompleted &&
              c.isEnabled &&
              c.questions
                .filter((catq) => catq.order < q.order)
                .every((q) => q.isComplete),
          })),
        }))
    )
  }

  /**
   * Gets the current quiz questions ordered by category and question order. 1 dimensional array
   * @returns {Array} ordered questions
   */
  get orderedQuizQuestions() {
    return this.quizCategories.reduce(
      (questions, category) => [...questions, ...category.questions],
      []
    )
  }

  /**
   * test is completed (all questions are answered)
   * @returns {Boolean} all questions are completed
   */
  get testIsCompleted() {
    return this.customerIsCompleted && this.orderedQuizQuestions.every((q) => q.isComplete)
  }


  /**
   * Customer is completed (email + name)
   */
  get customerProgress() {
    return (1 + (this.customer.name ? 1 : 0) + (validateEmail(this.customer.email) ? 1 : 0)) / 3
  }

  /**
   * Customer is completed (email + name)
   */
  get customerIsCompleted() {
    return this.customerProgress == 1
  }

  /**
   * Gets a question by its handle
   * @param {string} handle - question handle
   * @returns {Object} question
   */
  getQuestion(handle) {
    return this.orderedQuizQuestions.find((q) => q.handle === handle)
  }

  /**
   * Gets a question's responses by its handle
   * @param {string} questionHandle - question handle
   * @param {Object[]} responses optional - responses array
   * @returns {Object[]} responses
   * @returns {string} responses[] - response handle
   */
  getQuestionResponses(questionHandle, responses) {
    return (
      (responses ?? this.responses).find((r) => r.question === questionHandle)
        ?.selection ?? []
    )
  }

  /**
   * List of ordered completed questins as unidimentional array
   */
  get completedQuestions() {
    return this.orderedQuizQuestions.filter((q) => q.isComplete)
  }

  /**
   * List of all user's selections as unidimentional array
   */
  get selectedOptions() {
    return this.completedQuestions.reduce((options, question) => {
      return [
        ...options,
        ...question.options.filter((o) =>
          question.responses.includes(o.handle)
        ),
      ]
    }, [])
  }

  /**
   * List of all user's excluded selections as unidimentional array
   */
  get excludedOptions() {
    return this.completedQuestions.reduce((options, question) => {
      return [
        ...options,
        ...question.options.filter(
          (o) => !question.responses.includes(o.handle)
        ),
      ]
    }, [])
  }

  /**
   * Resulting list of producs based on user's selections
   */
  get recommendedProductVariants() {
    let selectedVariants = []
    
    this.quiz.rules.forEach((rule) => {
      const result = handleRuleResult(rule, this.selectedOptions)

      if(result === "INCLUDE") {
        rule.variants.forEach((v) => {
          if (!selectedVariants.map(v => v.id).includes(v.id)) {
            selectedVariants.push(v)
          }
        })
      }
      
      if (result === "EXCLUDE") {
        selectedVariants = selectedVariants.filter(v => !rule.variants.map(v => v.id).includes(v.id))
      }
    })


    return selectedVariants;
  }

  /**
   * Resulting list of producs based on user's selections
   */
  get selectedProductVariants() {
    const usageNotes = Object.assign(this.productUsageNotes)
    return this.recommendedProductVariants.map(v => ({
      ...v,
      notes: usageNotes[v.id] ?? []
    }))
  }

  /**
   * Retrieves usage notes for selected products and options
   */
  get productUsageNotes() {
    const usageNotes = {}
    const selectedOptions = Object.assign(this.selectedOptions)
    const recommendedProductVariants = Object.assign(this.recommendedProductVariants)
    
    this.quiz.usage_rules.forEach((rule) => {
      if (!rule.note) return;

      const result = handleRuleResult(rule, selectedOptions, recommendedProductVariants)
      
      rule.variants.forEach(({ id }) => {
        usageNotes[id] = usageNotes[id] || []

        if(result === "INCLUDE") {
          if (!usageNotes[id].includes(rule.note)) {
            usageNotes[id].push(rule.note)
          }
        }
        
        if (result === "EXCLUDE") {
          usageNotes[id] = usageNotes[id].filter(n => n !== rule.note)
        }
      })
    })

    // Show only last note
    Object.keys(usageNotes).forEach((id) => {
      usageNotes[id] = usageNotes[id].slice(-1)
    })

    return usageNotes
  }

  /**
   * Products ready to be added to order
   */
  get addedProductVariants() {
    const variants = Object.assign(this.selectedProductVariants)
    const orderLines = Object.assign(this.orderLines)
    return variants.filter((v) =>
      orderLines.map((l) => l.id).includes(v.id)
    )
  }

  /**
   * Order lines ready to be added to order with hydrated products
   */
  get hydratedOrderLines() {
    const variants = Object.assign(this.selectedProductVariants)
    const orderLines = Object.assign(this.orderLines)
    return orderLines
      .filter((l) => l.quantity)
      .map((l) => ({
        id: l.id,
        quantity: l.quantity,
        variant: variants.find((v) => v.id === l.id),
      }))
      .filter(l => l.variant)
  }

  /**
   * Adds a variant to order (not real order, just an internal state)
   *
   * @param {string} id
   * @param {number} quantity
   */
  addVariantToOrder(id, quantity = 1) {
    const orderLines = Object.assign(this.orderLines)

    if (!orderLines.map((l) => l.id).includes(id)) {
      this.orderLines = [...orderLines, { id, quantity }]
    } else {
      this.orderLines = orderLines.map((l) =>
        l.id === id ? { ...l, quantity: l.quantity + quantity } : l
      )
    }
  }

  /**
   * Syncs order lines with selected product variants
   */
  syncOrderLines() {
    const variantIdsAsOrderLines = Object.assign(this.orderLines.map((line) => line.id))

    this.selectedProductVariants.forEach(({ id, available }) => {
      if (available && !variantIdsAsOrderLines.includes(id)) {
        this.addVariantToOrder(id)
      } else if (!available && variantIdsAsOrderLines.includes(id)) {
        this.setOrderLineQuantity(id, 0)
      }
    })
  }

  /**
   * Sets variant quantity in internal order state
   *
   * @param {string} id variant/line id
   * @param {number} quantity
   */
  setOrderLineQuantity(id, quantity = 1) {
    this.orderLines = this.orderLines.map((l) =>
      l.id === id ? { ...l, quantity } : l
    )
  }

  /**
   * Removes all added order lines
   * @returns {void}
   */
  clearOrderLines() {
    this.orderLines = []
  }

  /**
   * Open Modal
   * @param {string} modalId
   */
  openModal(modalId, modalData = {}) {
    const onSummaryPage = window.location.href.includes('quiz#/summary')
    const exitingQuiz = modalId === 'close-warning'

    if (onSummaryPage && exitingQuiz) {
      this.closeQuiz()
      return
    }
    this.currentModalData = modalData
    this.modalBgVisible = true
    this.currentModal = modalId
  }

  /**
   * Close Modal
   */
  closeModal() {
    this.modalBgVisible = false
    setTimeout(() => {
      this.currentModal = null
    }, 300)
  }

  /**
   * Close Quiz
   * @returns {void}
   * @description removes local storage and redirects to home
   */
  closeQuiz() {
    this.killLocalStorage()
    window.location.href = '/'
  }

  /**
   * Removes local storage
   */
  killLocalStorage() {
    localStorage.removeItem(this.localStorageKey)
  }
}

export default new QuizState()
